import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChecklistItem } from '../../../types';
import { CheckCircle2, Circle, X, Icon } from '../../ui/Icon';

export interface PackingMember {
  id: string;
  label: string;
}

interface Props {
  item: ChecklistItem;
  /** Someone else's suitcase: shown, never edited. */
  readOnly: boolean;
  /** Only a shared section can say who brings the thing. */
  assignable: boolean;
  members: PackingMember[];
  onToggle: (checked: boolean) => void;
  onDelete: () => void;
  onAssign: (userId: string | null) => void;
  onRename: (text: string) => void;
}

export default function PackingItemRow({
  item, readOnly, assignable, members, onToggle, onDelete, onAssign, onRename,
}: Props) {
  const { t } = useTranslation();
  const assignee = members.find(m => m.id === item.assignedToUserId);

  // The draft doubles as the "is editing" flag: null = not editing, so there is
  // no second boolean that can disagree with it.
  const [draft, setDraft] = useState<string | null>(null);
  // Escape has to beat the blur it may trigger: otherwise the input losing
  // focus would commit the very draft the user just discarded.
  const discarded = useRef(false);

  function commit(value: string) {
    if (discarded.current) return;
    setDraft(null);
    const text = value.trim();
    if (text && text !== item.text) onRename(text);
  }

  function discard() {
    discarded.current = true;
    setDraft(null);
  }

  return (
    <div className="group flex items-center gap-3">
      <button
        onClick={() => onToggle(!item.checked)}
        disabled={readOnly}
        className="shrink-0 transition-transform hover:scale-110 disabled:hover:scale-100 disabled:cursor-default"
        aria-label={item.checked ? t('packing.uncheckItem') : t('packing.checkItem')}
      >
        {item.checked
          ? <Icon as={CheckCircle2} size={16} className="text-green-500" />
          : <Icon as={Circle} size={16} className="text-slate-300" />
        }
      </button>

      {draft !== null ? (
        <input
          type="text"
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={e => commit(e.currentTarget.value)}
          onKeyDown={e => {
            if (e.key === 'Enter')  commit(e.currentTarget.value);
            if (e.key === 'Escape') discard();
          }}
          aria-label={t('packing.editItem')}
          className="flex-1 min-w-0 bg-transparent border-b border-teal-400 text-sm py-0.5 text-slate-700 dark:text-slate-200 focus:outline-none"
        />
      ) : readOnly ? (
        <span className={`text-sm flex-1 transition-all ${item.checked ? 'line-through text-slate-400' : 'text-slate-700 dark:text-slate-300'}`}>
          {item.text}
        </span>
      ) : (
        <button
          type="button"
          onClick={() => { discarded.current = false; setDraft(item.text); }}
          title={t('packing.editItem')}
          className={`text-sm flex-1 min-w-0 text-left break-words cursor-text transition-all hover:underline decoration-dotted decoration-slate-300 underline-offset-4 ${item.checked ? 'line-through text-slate-400' : 'text-slate-700 dark:text-slate-300'}`}
        >
          {item.text}
        </button>
      )}

      {/* The shared list's real job: not one list everybody reads, but a split
          of who carries what. `checked` stays group state — whoever brings it
          ticks it — so there is no per-user check state behind this. */}
      {assignable && !readOnly && (
        <select
          value={item.assignedToUserId ?? ''}
          onChange={e => onAssign(e.target.value || null)}
          aria-label={t('packing.assignee.label')}
          className={`shrink-0 text-[11px] rounded-full border-0 py-0.5 pl-2 pr-5 cursor-pointer transition-colors focus:ring-1 focus:ring-teal-400 ${
            assignee
              ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 font-semibold'
              : 'bg-transparent text-slate-300 dark:text-slate-600 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100'
          }`}
        >
          <option value="">{t('packing.assignee.nobody')}</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
      )}
      {assignable && readOnly && assignee && (
        <span className="shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">
          {assignee.label}
        </span>
      )}

      {!readOnly && (
        <button
          onClick={onDelete}
          className="btn-icon w-5 h-5 md:opacity-0 md:group-hover:opacity-100 hover:text-red-400 transition-all"
          aria-label={t('packing.deleteItem')}
        >
          <Icon as={X} size={12} />
        </button>
      )}
    </div>
  );
}
